import React, { useState, useEffect } from 'react';
import { Switch } from './ui/switch';
import OnOffToggle from './OnOffToggle';
import { Label } from './ui/label';
import TimeSlotSelector from './TimeSlotSelector';
import InfoCard from './InfoCard';
import { SecondaryButton } from './Buttons';

/**
 * Visual schedule editor with day toggles
 * Used in: AddTeamModal, EditTeamModal
 */
const ScheduleEditor = ({ value = [], onChange }) => {
  const DAYS = [
    { value: 'monday', label: 'Mon' },
    { value: 'tuesday', label: 'Tue' },
    { value: 'wednesday', label: 'Wed' },
    { value: 'thursday', label: 'Thu' },
    { value: 'friday', label: 'Fri' },
    { value: 'saturday', label: 'Sat' },
    { value: 'sunday', label: 'Sun' }
  ];

  // Convert array to day-indexed object for easier manipulation
  const [schedule, setSchedule] = useState(() => {
    const scheduleObj = {};
    DAYS.forEach((day, index) => {
      const existing = value.find(slot => slot.day === day.value);
      scheduleObj[day.value] = existing 
        ? { enabled: true, startTime: existing.startTime, endTime: existing.endTime }
        : { enabled: index === 0, startTime: '09:00', endTime: '17:00' }; // Monday (index 0) enabled by default
    });
    return scheduleObj;
  });

  // Sync schedule object back to array format when it changes
  useEffect(() => {
    const scheduleArray = DAYS
      .filter(day => schedule[day.value]?.enabled)
      .map(day => ({
        day: day.value,
        startTime: schedule[day.value].startTime,
        endTime: schedule[day.value].endTime
      }));
    onChange(scheduleArray);
  }, [schedule]);

  const toggleDay = (dayValue, enabled) => {
    setSchedule(prev => ({
      ...prev,
      [dayValue]: { ...prev[dayValue], enabled }
    }));
  };

  const updateTime = (dayValue, field, value) => {
    setSchedule(prev => ({
      ...prev,
      [dayValue]: { ...prev[dayValue], [field]: value }
    }));
  };

  const toggleAllDaysAllTimes = (enabled) => {
    const newSchedule = {};
    DAYS.forEach(day => {
      newSchedule[day.value] = {
        enabled,
        startTime: '00:00',
        endTime: '23:59'  // True 24/7 coverage
      };
    });
    setSchedule(newSchedule);
  };

  const allDaysEnabled = DAYS.every(day => schedule[day.value]?.enabled);

  return (
    <div className="space-y-4">
      {/* Day Schedule Grid */}
      <div className="space-y-1">
        {DAYS.map((day) => {
          const daySchedule = schedule[day.value] || { enabled: false, startTime: '09:00', endTime: '17:00' };
          
          return (
            <div 
              key={day.value} 
              onClick={() => {
                if (!daySchedule.enabled) {
                  toggleDay(day.value, true);
                }
              }}
              className={`flex items-center gap-3 p-2 rounded transition-colors ${
                daySchedule.enabled 
                  ? 'bg-white cursor-default' 
                  : 'bg-gray-50 cursor-pointer hover:bg-gray-100'
              }`}
            >
              {/* Toggle - Responsive: Buttons on mobile, switch on desktop */}
              <div onClick={(e) => e.stopPropagation()}>
                <OnOffToggle
                  value={daySchedule.enabled}
                  onChange={(checked) => toggleDay(day.value, checked)}
                />
              </div>
              
              {/* Day Name */}
              <span className={`w-10 sm:w-12 font-medium text-xs sm:text-sm ${daySchedule.enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                {day.label}
              </span>
              
              {/* Time Selectors - responsive sizing */}
              <TimeSlotSelector
                value={daySchedule.startTime}
                onChange={(val) => {
                  if (!daySchedule.enabled) {
                    toggleDay(day.value, true);
                  }
                  updateTime(day.value, 'startTime', val);
                }}
                disabled={!daySchedule.enabled}
                placeholder="Start"
                className="flex-1 text-xs sm:text-sm"
              />
              
              <span className={`text-xs sm:text-sm ${daySchedule.enabled ? 'text-gray-500' : 'text-gray-300'}`}>to</span>
              
              <TimeSlotSelector
                value={daySchedule.endTime}
                onChange={(val) => {
                  if (!daySchedule.enabled) {
                    toggleDay(day.value, true);
                  }
                  updateTime(day.value, 'endTime', val);
                }}
                disabled={!daySchedule.enabled}
                placeholder="End"
                className="flex-1 text-xs sm:text-sm"
              />
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2 pt-2 border-t">
        <SecondaryButton
          type="button"
          size="sm"
          onClick={() => toggleAllDaysAllTimes(!allDaysEnabled)}
          className="flex-1"
        >
          {allDaysEnabled ? 'Clear All' : 'Available All Times'}
        </SecondaryButton>
        <SecondaryButton
          type="button"
          size="sm"
          onClick={() => {
            // Mon-Fri 9-5
            const newSchedule = {};
            DAYS.forEach(day => {
              const isWeekday = !['saturday', 'sunday'].includes(day.value);
              newSchedule[day.value] = {
                enabled: isWeekday,
                startTime: '09:00',
                endTime: '17:00'
              };
            });
            setSchedule(newSchedule);
          }}
          className="flex-1"
        >
          Mon-Fri 9-5
        </SecondaryButton>
      </div>
    </div>
  );
};

export default ScheduleEditor;
